import { parseContent, plainText, titleFromDoc } from './noteSerialize';

/**
 * My Notes, as rules.
 *
 * The three-panel shape (folders, list, editor) is only as good as what
 * decides which notes appear in the middle and in what order. That is all
 * here, apart from the markup, because it is the part somebody will notice
 * being wrong: a note that does not come back when searched for looks lost.
 */

export interface NoteFolder {
  id: string;
  name: string;
  parent_id: string | null;
}

export interface Note {
  id: string;
  folder_id: string | null;
  title: string;
  content: string;
  is_pinned: boolean;
  updated_at: string;
}

/** The pseudo-folder for "everything". Not a row — there is nothing to store. */
export const ALL_NOTES = 'all';

export function folderOf(note: Note): string {
  return note.folder_id ?? '';
}

/**
 * The notes shown for a folder selection.
 *
 * ALL_NOTES means everything the person has, including unfiled notes. A real
 * folder id means only that folder — and never the unfiled ones, which is the
 * mistake that makes a folder look like it contains things it does not.
 */
export function inFolder(notes: Note[], folderId: string): Note[] {
  if (folderId === ALL_NOTES) {
    return notes;
  }
  return notes.filter((note) => note.folder_id === folderId);
}

/**
 * The words a person actually wrote, with the document structure stripped.
 *
 * user_notes.content holds ProseMirror JSON since the Tiptap rewrite. Reading
 * that column as prose is wrong in two ways at once, and only one of them is
 * visible: the list previews render `{"type":"doc","content":[…` , and — far
 * worse — searching for "doc", "text", "type" or "paragraph" matches EVERY
 * note in the notebook, because those words are in the markup of all of them.
 *
 * parseContent also handles notes written before the editor existed, which
 * hold plain text, so this is the one door both eras go through.
 */
function readable(note: Note): string {
  return plainText(parseContent(note.content));
}

/**
 * Title and body, case-insensitively.
 *
 * The body is searched as well as the title because most notes are never
 * given a title — the first thing typed goes into the body, and a search that
 * only looked at titles would find nothing in a real notebook.
 */
export function searchNotes(notes: Note[], query: string): Note[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return notes;
  }
  return notes.filter(
    (note) =>
      note.title.toLowerCase().includes(needle) || readable(note).toLowerCase().includes(needle),
  );
}

/**
 * Newest first, and never mixed with the pinned ones.
 *
 * Sorted on a copy: sort mutates, and sorting the array a query cache handed
 * over reorders it underneath every other reader of the same cache entry.
 */
export function byRecency(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

export interface NoteSections {
  pinned: Note[];
  rest: Note[];
}

/** The middle panel: pinned above, everything else below, each newest-first. */
export function sectionNotes(notes: Note[]): NoteSections {
  return {
    pinned: byRecency(notes.filter((note) => note.is_pinned)),
    rest: byRecency(notes.filter((note) => !note.is_pinned)),
  };
}

/**
 * What the list shows for a note.
 *
 * An untitled note is the normal case, not an edge case — you open a new note
 * and start typing the body. "Untitled" in the list beats a blank row, and the
 * preview carries the actual content so the row is still identifiable.
 */
export function noteTitle(note: Note): string {
  // Three steps, in this order. An explicit title always wins — somebody who
  // named a note meant it, and deriving over the top would silently rename it
  // every time they edited the first line. Falling back to the first line is
  // what makes the title field optional rather than another empty box.
  const explicit = note.title.trim();
  if (explicit !== '') {
    return explicit;
  }
  const derived = titleFromDoc(parseContent(note.content), '').trim();
  return derived === '' ? 'Untitled note' : derived;
}

const PREVIEW_LENGTH = 80;

export function notePreview(note: Note): string {
  // Newlines collapse: a preview is one line, and a note beginning with a
  // blank line would otherwise preview as nothing at all.
  const flat = readable(note).replace(/\s+/g, ' ').trim();
  if (flat === '') {
    return 'No additional text';
  }
  return flat.length <= PREVIEW_LENGTH ? flat : `${flat.slice(0, PREVIEW_LENGTH).trimEnd()}…`;
}

/** Why a folder name is not acceptable, or null. */
export function folderNameProblem(
  name: string,
  existing: NoteFolder[],
  parentId: string | null = null,
): string | null {
  const trimmed = name.trim();
  if (trimmed === '') {
    return 'Give the folder a name.';
  }
  // SIBLINGS ONLY, because that is what the database enforces since folders
  // started nesting: note_folders_user_root_name_idx covers the root level and
  // note_folders_user_child_name_idx covers (user, parent, name). Comparing
  // against the whole list instead would refuse a perfectly legal "Drafts"
  // under Work because one already exists under Personal — a rule the person
  // is told about here but that nothing actually enforces, which is worse than
  // no check at all.
  const siblings = existing.filter((folder) => (folder.parent_id ?? null) === parentId);
  if (siblings.some((folder) => folder.name.trim().toLowerCase() === trimmed.toLowerCase())) {
    return 'You already have a folder with that name.';
  }
  return null;
}

// ── Time sections ──────────────────────────────────────────────────────────

export type TimeBucket = 'pinned' | 'today' | 'previous7' | 'previous30' | 'older';

export interface TimeSection {
  key: TimeBucket;
  label: string;
  notes: Note[];
}

const BUCKET_LABELS: Record<TimeBucket, string> = {
  pinned: 'Pinned',
  today: 'Today',
  previous7: 'Previous 7 Days',
  previous30: 'Previous 30 Days',
  older: 'Older',
};

/**
 * Which section a note belongs to, relative to a given "now".
 *
 * `now` is passed in rather than read from the clock so the boundaries are
 * testable and so every note in one render is bucketed against the SAME
 * instant — reading the clock per note puts two notes written a millisecond
 * apart either side of midnight.
 *
 * Boundaries are calendar days, not rolling 24-hour windows. Something
 * written at 11pm yesterday belongs under "Previous 7 Days" this morning, not
 * under "Today", because that is what a person means by today.
 */
export function bucketFor(updatedAt: string, now: Date): TimeBucket {
  const updated = new Date(updatedAt);
  if (Number.isNaN(updated.getTime())) {
    return 'older';
  }
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const stamp = updated.getTime();
  if (stamp >= startOfToday) return 'today';
  if (stamp >= startOfToday - 7 * dayMs) return 'previous7';
  if (stamp >= startOfToday - 30 * dayMs) return 'previous30';
  return 'older';
}

/**
 * The middle panel, in Apple Notes' shape.
 *
 * Pinned first as its own section regardless of age, then the time buckets.
 * Empty sections are dropped: a heading with nothing under it reads as
 * something having gone missing.
 */
export function timeSections(notes: Note[], now: Date): TimeSection[] {
  const pinned = byRecency(notes.filter((note) => note.is_pinned));
  const rest = notes.filter((note) => !note.is_pinned);

  const order: TimeBucket[] = ['today', 'previous7', 'previous30', 'older'];
  const sections: TimeSection[] = [];
  if (pinned.length > 0) {
    sections.push({ key: 'pinned', label: BUCKET_LABELS.pinned, notes: pinned });
  }
  for (const key of order) {
    const inBucket = byRecency(rest.filter((note) => bucketFor(note.updated_at, now) === key));
    if (inBucket.length > 0) {
      sections.push({ key, label: BUCKET_LABELS[key], notes: inBucket });
    }
  }
  return sections;
}
